import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ResponsePayload } from '../../../types';
import { CallbackSummaryQuestion } from '../../questions/CallbackSummaryQuestion';
import { registerQuestionRenderer, type QuestionRendererComponent } from './registry';

const CallbackSummaryRenderer: QuestionRendererComponent = ({
  question,
  existingResponse,
  isSubmitting,
  flowRunId,
  onDraftChange,
  onHasUserInputChange,
  onValidityChange,
}) => {
  const questionId = question?.id ?? existingResponse?.question ?? -1;

  const initialValue = useMemo(() => existingResponse?.text_value ?? '', [existingResponse]);
  const [textValue, setTextValue] = useState<string>(initialValue);

  const emit = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      const hasInput = trimmed.length > 0;

      onHasUserInputChange?.(hasInput);
      onValidityChange?.(hasInput);

      if (!hasInput) {
        onDraftChange(null);
        return;
      }

      const payload: ResponsePayload = {
        question: questionId,
        text_value: trimmed,
      };

      onDraftChange(payload);
    },
    [onDraftChange, onHasUserInputChange, onValidityChange, questionId],
  );

  useEffect(() => {
    setTextValue(initialValue);
    emit(initialValue);
  }, [emit, initialValue, questionId]);

  const handleTextChange = useCallback(
    (value: string) => {
      setTextValue(value);
      emit(value);
    },
    [emit],
  );

  return (
    <CallbackSummaryQuestion
      question={question}
      textValue={textValue}
      setTextValue={handleTextChange}
      isSubmitting={isSubmitting}
      flowRunId={flowRunId}
    />
  );
};

registerQuestionRenderer(['callback_summary'], CallbackSummaryRenderer);
